import { FileUp, Plus, Trash2 } from 'lucide-react';
import { FONT_NAMES, SIGNING_ORDER_MAX, SIGNING_ORDER_MIN, type Align } from '@tes/domain';
import { Labeled, NumberInput, Select, TextInput, Toggle } from '@tes/ui';
import { useEditor } from './editorStore';

export function SettingsPanel() {
  const setBaseFileOpen = useEditor((s) => s.setBaseFileOpen);
  const pageCount = useEditor((s) => s.pageSizes.length);
  // Select primitives individually — an object-returning selector would create a
  // new object each render and trip React 19's useSyncExternalStore loop guard.
  const name = useEditor((s) => s.name);
  const description = useEditor((s) => s.description);
  const requiresSignature = useEditor((s) => s.requiresSignature);
  const signingOrder = useEditor((s) => s.signingOrder);
  const style = useEditor((s) => s.defaultTextStyle);
  const roles = useEditor((s) => s.roles);
  const setSettings = useEditor((s) => s.setSettings);
  const setStyle = useEditor((s) => s.setStyle);
  const addRole = useEditor((s) => s.addRole);
  const updateRole = useEditor((s) => s.updateRole);
  const removeRole = useEditor((s) => s.removeRole);

  return (
    <div className="space-y-5 p-4">
      <div className="space-y-3">
        <Labeled label="Template name">
          <TextInput value={name} onChange={(e) => setSettings({ name: e.target.value })} />
        </Labeled>
        <Labeled label="Description">
          <TextInput
            value={description}
            onChange={(e) => setSettings({ description: e.target.value })}
          />
        </Labeled>
        <Toggle
          label="Requires signature"
          checked={requiresSignature}
          onChange={(v) => setSettings({ requiresSignature: v })}
        />
        <Labeled label={`Signing order (${SIGNING_ORDER_MIN}–${SIGNING_ORDER_MAX})`}>
          <NumberInput
            value={signingOrder}
            min={SIGNING_ORDER_MIN}
            max={SIGNING_ORDER_MAX}
            placeholder="—"
            onValueChange={(n) =>
              setSettings({
                signingOrder:
                  n === null || !Number.isInteger(n)
                    ? null
                    : Math.min(SIGNING_ORDER_MAX, Math.max(SIGNING_ORDER_MIN, n)),
              })
            }
          />
        </Labeled>
        <p className="text-[11px] text-slate-400">
          Position when an envelope shows several documents to the signer — lower numbers first,
          same number sorts alphabetically by title. Leave blank to sit at 3, so 1–2 come before
          it and 4–99 after.
        </p>
      </div>

      <div className="border-t border-slate-100 pt-4">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
          Base file
        </div>
        <p className="mb-2 text-[11px] text-slate-400">
          {pageCount} page{pageCount === 1 ? '' : 's'}. Swapping the PDF keeps every field exactly
          where it is.
        </p>
        <button
          onClick={() => setBaseFileOpen(true)}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-300 py-2 text-sm text-slate-700 hover:bg-slate-50"
        >
          <FileUp size={14} /> Replace base file…
        </button>
      </div>

      <div className="border-t border-slate-100 pt-4">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
          Default text style
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Labeled label="Font">
            <Select value={style.font} onChange={(e) => setStyle({ font: e.target.value })}>
              {FONT_NAMES.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          </Labeled>
          <Labeled label="Size (pt)">
            <NumberInput
              value={style.size}
              min={6}
              onValueChange={(n) => n !== null && setStyle({ size: Math.max(1, n) })}
            />
          </Labeled>
          <Labeled label="Color">
            <input
              type="color"
              value={style.color}
              onChange={(e) => setStyle({ color: e.target.value })}
              className="h-9 w-full rounded-md border border-slate-300"
            />
          </Labeled>
          <Labeled label="Align">
            <Select
              value={style.align}
              onChange={(e) => setStyle({ align: e.target.value as Align })}
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </Select>
          </Labeled>
        </div>
      </div>

      <div className="border-t border-slate-100 pt-4">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Signer roles
          </span>
          <button
            onClick={addRole}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-brand-600 hover:bg-brand-50"
          >
            <Plus size={14} /> Add role
          </button>
        </div>
        {roles.length === 0 && (
          <p className="text-[11px] text-slate-400">
            No roles yet. Signer fields need at least one role.
          </p>
        )}
        <div className="space-y-3">
          {roles.map((r, i) => (
            <div key={i} className="rounded-lg border border-slate-200 p-2">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs text-slate-400">Role {i + 1}</span>
                <button
                  onClick={() => removeRole(i)}
                  className="rounded p-1 text-red-500 hover:bg-red-50"
                  aria-label="Remove role"
                >
                  <Trash2 size={13} />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Labeled label="Key">
                  <TextInput
                    value={r.key}
                    onChange={(e) => updateRole(i, { key: e.target.value })}
                  />
                </Labeled>
                <Labeled label="Label">
                  <TextInput
                    value={r.label}
                    onChange={(e) => updateRole(i, { label: e.target.value })}
                  />
                </Labeled>
                <Labeled label="Default order">
                  <NumberInput
                    value={r.defaultOrder ?? null}
                    min={1}
                    onValueChange={(n) => updateRole(i, { defaultOrder: n })}
                  />
                </Labeled>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
